/**
 * English (en-US): the source catalogue. Brian's wording; no em dashes.
 * Length notes for translators on the tight ones (LOCALIZATION rule 1):
 *   [btn] a button, [tab] a short label, [seg] a chip. Allow +30% at most.
 */
export const en = {
  // ---- the voice ring (voice-ring.js) ----
  'voice.sheetTitle': 'Your voice profile',
  'voice.cardTitle': 'Complete your voice profile',
  'voice.cardFull': 'Your voice profile is complete',
  'voice.left': '{left} of {total} left',
  'voice.ofTotal': '{total} of {total}',
  'voice.complete': 'Complete',
  'voice.ringTitle': 'Voice profile: {left} of {total} left',
  'voice.ringFull': 'Your voice profile is complete',
  'voice.cardLine': 'Each answer helps Emma write scripts that sound like you, not like everyone else. About a minute each.',
  'voice.cardFullLine': 'Emma writes your scripts the way you talk. Every answer you gave is part of it.',
  'voice.ecardLine': '{left} of {total} left. Each one helps me sound like you.',
  'voice.answerOne': 'Answer one', // [btn]
  'voice.doneForNow': 'Done for now', // [btn]
  'voice.loading': 'One moment.',
  'voice.labelTopic': 'About {topic}',
  'voice.labelTake': 'Pretend you hit record',
  'voice.labelOwn': 'In your own words',
  'voice.typePlaceholder': 'Type it the way you’d say it',
  'voice.yourAnswer': 'Your answer',
  'voice.noMic': 'No microphone here, so type it the way you’d say it.',
  'voice.startAnswering': 'Start answering',
  'voice.stop': 'Stop',
  'voice.tapToStart': 'Tap to start. Tap again when you’re done.',
  'voice.listening': 'Listening. Take your time.',
  'voice.wrapUp': 'Take a few seconds to wrap up.',
  'voice.heardNothing': 'I didn’t catch that. Tap to try again, or type it.',
  'voice.skipOne': 'Skip this one', // [btn]
  'voice.typeInstead': 'Type instead', // [btn]
  'voice.talkInstead': 'Talk instead', // [btn]
  'voice.next': 'Next', // [btn]
  'voice.done': 'Done', // [btn]
  'voice.skipForNow': 'Skip for now', // [btn]
  'voice.ack0': 'Got it.',
  'voice.wholeTitle': 'That’s your whole voice.',
  'voice.wholeBody': 'Every script from here on is grown from all of it. Add a topic and it grows a little more.',
  'voice.allForNowTitle': 'That’s all for now.',
  'voice.allForNowBody': 'The ones you skipped wait here. They’ll come back another day.',
  'voice.backToIt': 'Back to it', // [btn]
  'voice.picksLabel': 'A few quick picks',
  'voice.picksLede': 'Tap what sounds like you. Anything you leave alone stays out of your scripts.',
  'voice.picksEnding': 'Which is closer to how you’d end a video about {topic}?',
  'voice.picksNeither': 'Neither. I’d say it my way.',
  'voice.picksAudience': 'What do you call the people watching?',
  'voice.picksCaption': 'Which caption could you have typed?',
  'voice.picksSwear': 'Swearing in your posts',
  'voice.picksPhrases': 'These phrases: tap once for a lot, twice for sometimes, three times for never',
  // ---- Emma's offer ----
  'voice.offerBrowse': 'Got two minutes? A few more answers and your scripts need fewer edits.',
  'voice.offerDay': 'Your voice is about {pct}% grown. {left} more answers and I stop guessing how you’d say it.',
  'voice.offerBuild': 'Nice pick. Answer one voice question and the next script lands even closer to how you talk.',
  'voice.offerAlmost': 'Almost there. {left} to go and your voice is fully grown.',
  'voice.later': 'Later', // [btn]
  'voice.swipeAway': 'Swipe away for later',
  'voice.stopReminding': 'Stop reminding me',
  // ---- onboarding's voice questions (VoiceInterview) ----
  'iv.loading': 'Loading your questions...',
  'iv.soundOn': 'Sound on', // [btn]
  'iv.soundOff': 'Sound off', // [btn]
  'iv.takeEyebrow': 'Pretend you hit record',
  'iv.questionOf': 'Question {n} of {total}',
  'iv.sayAgain': 'Say it again', // [btn]
  'iv.placeholder': 'Answer the way you talk, like a voice memo to a friend.',
  'iv.sayInstead': 'Say it instead', // [btn]
  'iv.stopTalking': 'Stop talking', // [btn]
  'iv.back': 'Back', // [btn]
  'iv.finishLater': 'Finish the rest later',
  'iv.readTitle': 'Here’s what I heard.',
  'iv.readLede': 'I’ll turn your answers into your voice profile. Every story you build gets written in it.',
  'iv.listeningBack': 'Listening back to your {n} answers…',
  'iv.needMore': 'Answer at least {min} to start your voice profile, or go on and answer them later in the app.',
  'iv.makeProfile': 'Make my voice profile', // [btn]
  'iv.writing': 'Writing your voice profile', // [btn]
  'iv.finishing': 'Finishing', // [btn]
  'iv.takesMinute': 'This takes up to a minute.',
  'iv.skipSaved': 'Skip this for now. Your answers are saved.',
  'iv.youSay': 'You say',
  'iv.youNever': 'You never say',
  'iv.defaultPosition': 'Your default position: {pos}. You can set it per topic in Topics.',
  'iv.seedling': 'This is your voice as a seedling. It grows with every answer.',
  'iv.coin': 'You earned a coin: True Voice.',
  'iv.readSpoken': 'This is your starting voice. It gets closer with every answer.',
  'iv.ringStarted': 'Voice profile started: {left} of {total} left',
  'iv.ringBody': 'The rest grow in the app: there’s a ring around me, and I’ll ask now and then when you’ve got a minute. One about each of your topics, a few more like these, and some quick picks.',
  'iv.ringSkipped': 'Anything you skipped waits at the back of the line.',
  // ---- Settings (profile.html) ----
  'settings.voiceProfile': 'Your voice profile',
  'settings.anglePerTopic': 'Your angle per topic',
  'settings.changeInTopics': 'Change in Topics',
  'settings.redoStarter': 'Answer the first five again',
  'settings.redoStarterLine': 'The five questions from setup, with your answers ready to edit. The rest live in the ring on Emma.',
  'settings.voiceSaved': 'Your voice profile is saved.',
} as const

export type StringKey = keyof typeof en
